import { z } from 'zod';
import { SHIPMENT_TRANSITIONS } from './shipment-lifecycle-policy.js';
import { normalizePersistedTimestamp, persistedTimestampSchema } from './firestore-timestamp.schema.js';
import { asDomainRecord } from './domain-value.js';
import { HEAVY_TIMELINE, STANDARD_TIMELINE, normalizeShipmentPhase, normalizeShipmentStatus, shipmentPhaseSchema, } from './shipment-status.js';
// Projection interne d'affichage. Elle n'est volontairement pas exportée :
// les transitions restent l'autorité opérationnelle des phases persistées.
const SHIPMENT_PHASE_PROGRESS = {
    order: 5, preparation: 10, dpd_collection: 15, collected_europe: 25,
    vm_reception: 10, vm_unidentified: 5, heavy_processing: 10, heavy_collection: 20,
    warehouse: 40, befret_transit: 60, congo_distribution: 80,
    heavy_delivery: 85, delivered: 100, return_to_sender: 90, incident: 100,
};
const EXPLICIT_PHASES = {
    order_created: 'order', payment_pending: 'order', dpd_creation_in_progress: 'dpd_collection',
    dpd_creation_failed: 'dpd_collection', ready_for_deposit: 'dpd_collection',
    dpd_customs_check: 'collected_europe', dpd_incident_eu: 'collected_europe',
    held_at_dpd_depot: 'collected_europe', warehouse_palletized: 'warehouse',
    ready_for_befret: 'warehouse', customs_clearance: 'congo_distribution',
    seized_customs_eu: 'incident', seized_customs_congo: 'incident', poorly_packed: 'incident',
    dangerous_goods: 'incident', heavy_request_received: 'heavy_processing',
    heavy_analysis_in_progress: 'heavy_processing', heavy_specialist_assigned: 'heavy_processing',
    heavy_transport_planned: 'heavy_processing', heavy_team_dispatched: 'heavy_collection',
    heavy_on_site_assessment: 'heavy_collection', heavy_loading_in_progress: 'heavy_collection',
    heavy_delivery_scheduled: 'heavy_delivery', heavy_delivery_team_assigned: 'heavy_delivery',
    heavy_out_for_delivery: 'heavy_delivery', heavy_delivery_attempted: 'heavy_delivery',
    heavy_delivery_rescheduled: 'heavy_delivery', heavy_delivered_with_installation: 'delivered',
    vm_package_received: 'vm_reception', shipping_payment_pending: 'vm_reception',
    consolidation_payment_pending: 'vm_reception', assisted_draft: 'order',
    assisted_sms_sent: 'order', assisted_expired: 'incident', assisted_cancelled: 'incident',
    cancelled: 'incident',
};
export function deriveShipmentPhase(statusValue, fallback = null) {
    const status = normalizeShipmentStatus(statusValue);
    if (!status)
        return normalizeShipmentPhase(fallback);
    return SHIPMENT_TRANSITIONS[status]?.phase ?? EXPLICIT_PHASES[status] ?? normalizeShipmentPhase(fallback);
}
export function deriveShipmentProgress(statusValue, phaseValue) {
    const status = normalizeShipmentStatus(statusValue);
    if (status === 'delivered' || status === 'heavy_delivered_with_installation' || status === 'return_completed')
        return 100;
    const phase = deriveShipmentPhase(status, phaseValue);
    return phase ? SHIPMENT_PHASE_PROGRESS[phase] : 0;
}
export const domainTimelineEntrySchema = z
    .object({
    phase: shipmentPhaseSchema, label: z.string(), description: z.string(), completed: z.boolean(),
    completedAt: z.union([persistedTimestampSchema, z.string().min(1)]).optional(),
    estimatedAt: z.union([persistedTimestampSchema, z.string().min(1)]).optional(),
})
    .strict();
/**
 * Compatibilité legacy en lecture uniquement : les premières timelines VM
 * étaient des maps indexées, alors que le contrat canonique est un tableau.
 */
export const shipmentTimelineLegacyReadSchema = z.union([
    z.array(z.record(z.unknown())),
    z.record(z.record(z.unknown())),
]);
/**
 * Convertit les timelines legacy (tableau libre ou map VM) vers la séquence
 * canonique du flux. Les libellés et dates existants sont conservés afin de ne
 * pas réécrire l'historique affiché ; même une ancienne date non ISO reste
 * lisible, mais ne fait pas partie du schéma des nouvelles écritures.
 */
export function normalizeShipmentTimeline(raw, category = 'standard') {
    const values = Array.isArray(raw) ? raw : Object.values(asDomainRecord(raw) ?? {});
    const existing = new Map();
    for (const item of values) {
        const value = asDomainRecord(item);
        const phase = normalizeShipmentPhase(value?.phase ?? value?.key ?? value?.status);
        if (value && phase && !existing.has(phase))
            existing.set(phase, value);
    }
    const base = category === 'heavy' ? HEAVY_TIMELINE : category === 'virtual_mailbox'
        ? [['vm_reception', 'Reçu à la Virtual Mailbox', 'Colis identifié et prêt à être traité'], ...STANDARD_TIMELINE.slice(2)]
        : STANDARD_TIMELINE;
    return base.map(([phase, label, description]) => {
        const previous = existing.get(phase);
        const normalizedCompletedAt = normalizePersistedTimestamp(previous?.completedAt);
        const normalizedEstimatedAt = normalizePersistedTimestamp(previous?.estimatedAt);
        const completedAt = normalizedCompletedAt ??
            (typeof previous?.completedAt === 'string' && previous.completedAt ? previous.completedAt : null);
        const estimatedAt = normalizedEstimatedAt ??
            (typeof previous?.estimatedAt === 'string' && previous.estimatedAt ? previous.estimatedAt : null);
        return {
            phase, label: typeof previous?.label === 'string' ? previous.label : label,
            description: typeof previous?.description === 'string' ? previous.description : description,
            completed: previous?.completed === true,
            ...(completedAt != null ? { completedAt } : {}),
            ...(estimatedAt != null ? { estimatedAt } : {}),
        };
    });
}
export const shipmentTimelineCategorySchema = z.enum(['standard', 'heavy', 'virtual_mailbox']);
export const deriveShipmentTimelineInputSchema = z
    .object({
    status: z.unknown(), phase: z.unknown().optional(), category: shipmentTimelineCategorySchema.optional(),
    timeline: z.unknown().optional(), completedAt: z.unknown().optional(),
})
    .strict();
export function deriveShipmentTimeline(input) {
    const current = deriveShipmentPhase(input.status, input.phase);
    const progress = deriveShipmentProgress(input.status, current);
    return normalizeShipmentTimeline(input.timeline, input.category).map(entry => {
        const phaseProgress = SHIPMENT_PHASE_PROGRESS[entry.phase];
        const completed = entry.completed || phaseProgress < progress || progress === 100;
        const completedAt = normalizePersistedTimestamp(input.completedAt);
        return completed && !entry.completedAt && entry.phase === current && completedAt != null
            ? { ...entry, completed, completedAt }
            : { ...entry, completed };
    });
}
export const CLIENT_TIMELINE_PHASES = ['order', 'collected_europe', 'warehouse', 'befret_transit', 'congo_distribution', 'delivered'];
export const clientTimelinePhaseSchema = z.enum(CLIENT_TIMELINE_PHASES);
/**
 * #1430 — la frise client ne connaît que SIX étapes ; le backend écrit un
 * vocabulaire de phases beaucoup plus large (`ShipmentPhase`). Sans traduction,
 * `canonicalTimelinePhase` rend `null` et l'étape ne s'affiche pas : mesuré le
 * 28/08/2026 sur la production, **118 colis sur 212** portaient une
 * `phase.current` que la frise ne savait pas rendre.
 *
 * 🔴 La donnée n'est PAS corrompue : `dpd_collection` est la valeur CORRECTE de
 * l'enum pour un colis en `ready_for_deposit`. Un backfill aurait détruit une
 * information juste pour la faire entrer de force dans un vocabulaire plus
 * étroit — d'où la traduction ici, et **zéro écriture en base**.
 *
 * ── Le principe de traduction ─────────────────────────────────────────────
 * Chaque phase large est ramenée à **la dernière étape dont le client peut
 * être SÛR**, jamais à la suivante. C'est ce qui interdit d'annoncer « collecté
 * en Europe » à quelqu'un qui a encore son colis à la maison : `dpd_collection`
 * couvre `label_generated` et `ready_for_deposit`, où c'est précisément LE
 * CLIENT qui doit encore agir. Le mensonge inverse — annoncer une étape déjà
 * franchie — est celui qui a coûté #1574 (« en cours de livraison » sur un
 * colis encore en Europe).
 */
export const TIMELINE_PHASE_ALIASES = {
    // Formes historiques (antérieures au vocabulaire actuel).
    order_confirmed: 'order', warehouse_befret: 'warehouse', shipped_to_congo: 'befret_transit', arrived_congo: 'congo_distribution', delivered_final: 'delivered',
    // #1430 — phases larges du backend (`ShipmentPhase`), absentes de la frise.
    // Commande passée, rien n'a encore quitté les mains du client :
    preparation: 'order', // paiement → création DPD
    dpd_collection: 'order', // étiquette émise, dépôt/collecte en attente
    heavy_processing: 'order', // analyse + devis d'un colis lourd
    heavy_collection: 'order', // collecte spécialisée pas encore effectuée
    // Le colis est physiquement chez BeFret :
    vm_reception: 'warehouse',
    vm_consolidation: 'warehouse',
    vm_unidentified: 'warehouse',
    // Livraison finale en RDC :
    heavy_delivery: 'congo_distribution',
};
export const STATUS_TO_CLIENT_TIMELINE_PHASE = {
    received_at_warehouse: 'warehouse', warehouse_sorted: 'warehouse', destination_sorted: 'warehouse', preparation_blocked: 'warehouse',
    befret_transit: 'befret_transit', local_sorting: 'congo_distribution', delivery_sorted: 'congo_distribution', out_for_delivery: 'congo_distribution', awaiting_pickup: 'congo_distribution', delivery_attempted: 'congo_distribution', delivery_rescheduled: 'congo_distribution',
    delivered: 'delivered', returned_to_sender: 'incident', return_received_at_warehouse: 'incident', return_completed: 'incident', lost: 'incident', damaged: 'incident',
};
export const phaseTimelineStepSchema = z
    .object({
    phase: z.string().optional(), label: z.string().optional(), description: z.string().optional(),
    completed: z.boolean().optional(), completedAt: z.string().datetime().optional(),
})
    .strict();
export function canonicalTimelinePhase(phase) {
    if (typeof phase !== 'string')
        return null;
    return CLIENT_TIMELINE_PHASES.includes(phase) ? phase : TIMELINE_PHASE_ALIASES[phase] ?? null;
}
export function computePhaseTimelineUpdates(existingTimeline, targetStatus, nowISO) {
    const reached = STATUS_TO_CLIENT_TIMELINE_PHASE[targetStatus];
    if (!reached)
        return {};
    if (reached === 'incident')
        return { 'phase.current': 'incident' };
    const targetRank = CLIENT_TIMELINE_PHASES.indexOf(reached);
    const updates = { 'phase.current': reached };
    const steps = Array.isArray(existingTimeline) ? existingTimeline : [];
    if (!steps.length) {
        updates['phase.progress'] = Math.round((targetRank + 1) / CLIENT_TIMELINE_PHASES.length * 100);
        return updates;
    }
    let completed = 0;
    const timeline = steps.map(step => {
        const rank = CLIENT_TIMELINE_PHASES.indexOf(canonicalTimelinePhase(step?.phase));
        if (rank >= 0 && rank <= targetRank && !step?.completed) {
            completed++;
            return { ...step, completed: true, completedAt: nowISO };
        }
        if (step?.completed)
            completed++;
        return step;
    });
    updates['phase.timeline'] = timeline;
    updates['phase.progress'] = Math.round(completed / timeline.length * 100);
    return updates;
}
